import {
  ApplicationVerifier,
  ConfirmationResult,
  User,
  UserCredential,
  UserInfo,
} from "@firebase/auth-types";
import { DocumentReference, Query } from "@firebase/firestore-types";
import { Users } from "@just-in-types";
import { AnyAction, Dispatch } from "redux";
import store from "..";
import { cleanMobile } from "../../adminFunctions/cleaningFunctions";
import {
  auth,
  facebookProvider,
  firestore,
  googleProvider,
} from "../../firebase";
import { getBusinesses } from "./businessesActions";
import { setRoutes } from "./routerActions";

const usersRef = firestore.collection("users");

export const emptyUser: Users.EmptyUser = {
  covidquestions: null,
  dateofbirth: null,
  email: null,
  hasAccount: true,
  hasPaidToday: false,
  ismask:false,
  language: null,
  mobile: null,
  mugshot: null,
  mugshotUrl: null,
  name: { displayName: null, firstname: null, lastname: null },
  receiptCode: null,
  secretMobile: null,
  signature: null,
  signatureUrl: null,
  supercard: null,
  vehicles: [],
  zip: null,
};

export const createAccountWithEmail = async (
  email: string,
  password: string
) => {
  try {
    const result: UserCredential = await auth.createUserWithEmailAndPassword(
      email,
      password
    );

    return result;
  } catch (error) {
    return error.code as string;
  }
};

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const result: UserCredential = await auth.signInWithEmailAndPassword(
      email,
      password
    );

    return result;
  } catch (error) {
    return error.code as string;
  }
};

export const signInWithFacebook = async () => {
  try {
    const result: UserCredential = await auth.signInWithPopup(facebookProvider);

    return result;
  } catch (error) {
    return error.code as string;
  }
};

export const signInWithGoogle: SignInWithGoogle = () => {
  auth.signInWithPopup(googleProvider);

  return auth.getRedirectResult();
};

export const signInWithMobile = async (
  mobile: string,
  applicationVerifier: ApplicationVerifier
) => {
  try {
    const formatted = `+1${mobile}`;
    const result: ConfirmationResult = await auth.signInWithPhoneNumber(
      formatted,
      applicationVerifier
    );

    return result;
  } catch (error) {
    return error.code as string;
  }
};

export const confirmSignInWithMobile = async (
  confirmation: string,
  confirmationResult: ConfirmationResult
) => {
  try {
    const result: UserCredential = await confirmationResult.confirm(
      confirmation
    );

    return result;
  } catch (error) {
    return error.code as string;
  }
};

export const SIGN_OUT = "users.SIGN_OUT";
export const signOut = () => {
  auth.signOut();

  store.dispatch({ type: SIGN_OUT });
};

export const SET_PERMISSIONS = "users.SET_PERMISSIONS";
export const setPermissions = (user: Users.UserWithAccount) =>
  store.dispatch({
    payload: user.accountData.permissions,
    type: SET_PERMISSIONS,
  });

export const WATCH_FOR_CURRENT_USER = "users.WATCH_FOR_USER";
export const watchForCurrentUser: WatchForCurrentUser = () => (
  dispatch: Dispatch
) =>
  auth.onAuthStateChanged(async (firebaseUser) => {
    if (firebaseUser) {
      const user = await processUser(firebaseUser);
      const {
        accountData: { permissions },
      } = user;

      getBusinesses(permissions)(dispatch);
      setPermissions(user);
      setRoutes(user);
      subscribeToUser(user.userId, dispatch);

      return dispatch({
        payload: user,
        type: WATCH_FOR_CURRENT_USER,
      });
    } else {
      return dispatch({ payload: null, type: WATCH_FOR_CURRENT_USER });
    }
  });

export const SUBSCRIBE_TO_USER = "users.SUBSCRIBE_TO_USER";
export const subscribeToUser = (userId: string, dispatch: Dispatch) => {
  const subscription = usersRef.doc(userId).onSnapshot(
    (snapshot) => {
      const user = snapshot.data() as undefined | Users.UserWithAccount;

      if (user) {
        dispatch({
          payload: { subscription, user },
          type: SUBSCRIBE_TO_USER,
        });
      }
    },
    (error) => {
      console.error(error);
    }
  );
};

export const processProviderData = (providerData: Array<null | UserInfo>) => {
  const empty = {
    displayName: null,
    email: null,
    phoneNumber: null,
    photoURL: null,
    providerId: "just-in",
    uid: null,
  };

  if (providerData) {
    return providerData.reduce((curr, acc) => {
      const processKey = (key: keyof UserInfo) =>
        curr
          ? curr[key]
            ? curr[key]
            : acc
            ? acc[key]
            : empty[key]
          : empty[key];

      return {
        displayName: processKey("displayName"),
        email: processKey("email"),
        phoneNumber: processKey("phoneNumber"),
        photoURL: processKey("photoURL"),
        providerId: "just-in",
        uid: processKey("uid"),
      };
    }, empty);
  } else {
    return empty;
  }
};

const processUserData = (
  accountData: Users.AccountData,
  {
    displayName,
    phoneNumber,
    email,
    emailVerified,
    isAnonymous,
    metadata: { creationTime, lastSignInTime },
    photoURL,
    uid,
  }: User,
  {
    language: userLanguage,
    mobile: userMobile,
    name: { firstname, lastname },
    secretMobile: userSecretMobile,
    userId,
    ...userData
  }: (Users.EmptyUser & { userId: string }) | Users.User
): Users.UserWithAccount => {
  const {
    mobile: firebaseMobile,
    secretMobile: firebaseSecretMobile,
  } = cleanMobile(phoneNumber);
  const language = userLanguage || "eng";
  const mobile = userMobile || firebaseMobile;
  const secretMobile = userSecretMobile || firebaseSecretMobile;

  return {
    accountData: {
      ...accountData,
      uid: userId,
    },
    email: email || null,
    firebaseData: {
      displayName,
      email,
      emailVerified,
      isAnonymous,
      metadata: {
        creationTime,
        lastSignInTime,
      },
      phoneNumber,
      photoURL,
      uid,
    },
    hasAccount: true,
    language,
    mobile,
    name: {
      displayName,
      firstname: firstname || null,
      lastname: lastname || null,
    },
    secretMobile,
    uid,
    userId,
    ...userData,
  };
};
export const updateUser = ({
  userId,
  update,
}: {
  userId: string;
  update: { [field: string]: any };
}) =>
  usersRef
    .doc(userId)
    .update(update)
    .catch((error) => console.error(error));

export const setUserDocument = ({
  user,
  userRef,
}: {
  user: Users.UserWithAccount;
  userRef: DocumentReference;
}) =>
  userRef.set(user, { merge: true }).catch((error) => {
    console.error(error);
  });

export function isDocumentReference(value: any): value is DocumentReference {
  return typeof (value as DocumentReference).collection === "function";
}

export const processUser = async (firebaseData: User) => {
  const {
    displayName,
    email,
    phoneNumber,
    photoURL,
    providerId,
  } = processProviderData(firebaseData.providerData);
  const uid = firebaseData.uid;
  const accountData: Users.AccountData = {
    displayName,
    email,
    permissions: {
      businesses: [],
      ismask:false,
      role: "user",
      sites: [],
    },
    phoneNumber,
    photoURL,
    providerId,
    uid: uid || "unknown",
  };
  const userQuery: DocumentReference | Query = usersRef.doc(uid);
  let userData: null | Users.User = null;
  let userId = userQuery.id;

  const doc = await userQuery.get();

  if (doc.exists) {
    userData = doc.data() as Users.User | Users.UserWithAccount;
    userId = doc.id;
  }

  if (userData) {
    const userRef = usersRef.doc(userId);
    const user = processUserData(accountData, firebaseData, {
      userId,
      ...userData,
    });

    await setUserDocument({ user, userRef });

    return user;
  } else {
    const user = processUserData(accountData, firebaseData, {
      userId,
      ...emptyUser,
    }) as Users.UserWithAccount;

    await setUserDocument({ user, userRef: usersRef.doc(userId) });

    return user;
  }
};

export const ADD_VEHICLE_PICK = "users.addVehiclePick";
export const addVehiclePick = (userId: string, vehicle: Users.VehiclePick) => (
  dispatch: Dispatch
) =>
  firestore.runTransaction(async (transaction) => {
    try {
      const userRef = usersRef.doc(userId);
      const doc = await transaction.get(userRef);
      const userData = doc.data() as Users.User;
      const vehicles = [...userData.vehicles];

      if (
        !vehicles.some(
          ({ businessId, make, model, siteId, yearMax, yearMin }) =>
            vehicle.businessId === businessId &&
            vehicle.make === make &&
            vehicle.model === model &&
            vehicle.siteId === siteId &&
            vehicle.yearMax === yearMax &&
            vehicle.yearMin === yearMin
        )
      ) {
        vehicles.push(vehicle);

        dispatch({ payload: vehicle, type: ADD_VEHICLE_PICK });
      } else {
        dispatch({ payload: "no vehicle added", type: ADD_VEHICLE_PICK });
      }

      return transaction.update(userRef, { vehicles });
    } catch (error) {
      throw new Error(error);
    }
  });

export const redeemCoupon = async ({
  couponId,
  userId,
}: {
  couponId: string;
  userId: string;
}) => {
  try {
    return firestore
      .collection(`users/${userId}/coupons`)
      .doc(couponId)
      .update({
        redemption_date: new Date(),
        redemption_flag: true,
      });
  } catch (error) {
    throw new Error(error);
  }
};

export const makeAccessRequest = async ({
  businessId,
  currentUser,
  name,
  role,
  siteId,
}: {
  businessId: string;
  currentUser?: null | firebase.auth.UserCredential;
  name: { firstname: string; lastname: string };
  role: string;
  siteId: string;
}) => {
  const user = currentUser ? currentUser.user : auth.currentUser;

  if (user) {
    try {
      const { uid } = user;

      await firestore
        .collection("userRequests")
        .doc(uid)
        .set({ approved: false, businessId, name, role, siteId });

      return true;
    } catch (error) {
      console.error(error);

      return false;
    }
  } else {
    return false;
  }
};

export type CreateAccountWithEmail = (
  email: string,
  password: string
) => () => Promise<string | UserCredential>;
export type SignInWithEmail = (
  email: string,
  password: string
) => () => Promise<string | UserCredential>;
export type SignInWithMobile = (
  mobile: string,
  applicationVerifier: ApplicationVerifier
) => Promise<string | ConfirmationResult>;
export type SignInWithFacebook = () => Promise<string | UserCredential>;
export type SignInWithGoogle = () => Promise<any>;
export type SignOut = () => Promise<any>;
export type WatchForCurrentUser = () => void;
export type AddVehiclePick = (
  userId: string,
  vehicle: Users.VehiclePick
) => (dispatch: Dispatch<AnyAction>) => Promise<any>;



// WEBPACK FOOTER //
// ./src/store/actions/usersActions.ts